import { EasyLifeLicenseSku } from '@easylife365/react';
import { KeyCredentialDto, PasswordCredentialDto } from './graph.types';
import { sameId } from '../lib/ids';

export type IsAppEnabledResponse = {
  isEnabled: boolean;
  isOnboarded: boolean;
  isBlocked: boolean;
  expirationInfo?: ExpirationInfo;
  sku: EasyLifeLicenseSku;
  errorCode?: string;
  settings: AppsSettings;
  managedAppInfo: ManagedAppInfoDto;
  tenantId: string;
};

export type IsAppOnboardedRequest = {
  tenantId: string;
};

export type IsAppOnboardedResponse = {
  isOnboarded: boolean;
};

export type AppsSettings = {
  expirationThresholdDays: number;
  samlExpirationThresholdDays: number;
  hideEntraIdLinksForNonAdmins: boolean;
  showAllAppsAccessMode: ShowAllAppsAccessMode;
  showAllAppsSecurityGroupId?: string | null;
};

export type ExpirationInfo = {
  remainingDays: number;
  isExpired: boolean;
};

export type ManagedAppInfoDto = {
  managedAppIds: string[];
  unmanagedAppIds: string[];
  managedAppOwnerIds: string[];
};

export type TaskAssigneeDto = {
  id: string;
  displayName?: string;
  mail?: string;
  userPrincipalName?: string;
};

/** `restricted` is false where the tenant has not granted the permission the rule needs: any enabled member may be assigned. */
export type TaskAssigneesDto = {
  restricted: boolean;
  assignees: TaskAssigneeDto[];
};

export type IdentityTaskDto = {
  id: string;
  status: IdentityTaskStatus;
  ownerId?: string;
  /** Missing on rows stored before the application id was recorded. */
  appObjectId?: string | null;
  credentialType: CredentialType;
  comment?: string;
};

export type UserSettingsDto = {
  technicalOwnershipSettings: UserOwnershipSettingsDto;
  businessOwnershipSettings: UserOwnershipSettingsDto;
};

export type UserOwnershipSettingsDto = {
  ownerApps: Record<string, OwnerAppInfoDto>;
};

export type OwnerAppInfoDto = {
  appType: ResourceType;
};

export type CredentialTaskDto = {
  thumbprint?: string | null;
  keyId: string;
  appObjectId?: string | null;
  appId: string;
  appDisplayName?: string | null;
  displayName?: string | null;
  value?: string | null;
  resourceType: ResourceType;
  startDate?: Date | undefined;
  type: CredentialType;
  expires?: Date | undefined;
  status?: IdentityTaskStatus;
  ownerId?: string;
  assignMyself?: boolean;
  comment?: string;
  logoUrl?: string | null;
  isSaml: boolean;
};

export type AppSettingsDto = {
  appType?: ResourceType | null;
  notificationSettings: AppNotificationSettingsDto;
  technicalOwnershipSettings: AppOwnershipSettingsDto;
  businessOwnershipSettings: AppOwnershipSettingsDto;
  isManaged?: boolean;
  helpLinks: HelpLinkSettingsDto;
  policy: PolicyExtension;
  notes?: string | null;
  /** When the activity scan last saw a sign-in. Written by the scan only; a save never changes it. */
  lastSignInDateTime?: string;
};

export type AppLastSignInDto = {
  id: string;
  lastSignInDateTime: string;
};

export type AppNotificationSettingsDto = {
  isEnabled?: boolean;
  snoozingJustification?: string;
};

export type AppOwnershipSettingsDto = {
  owners: SecondaryOwnerDto[];
  permissions: PermissionSet;
};

export type SecondaryOwnerDto = {
  id: string;
};

export type AddSecondaryOwnerRequest = {
  appType: ResourceType;
  owner: SecondaryOwnerDto;
};

export type HelpLinkSettingsDto = {
  overviewLink?: string;
  certificatesLink?: string;
  secretsLink?: string;
  ownersLink?: string;
  settingsLink?: string;
  rulesLink?: string;
};

export type PolicyExtension = {
  pId?: string;
  owStCh?: string;
  owSt?: string;
  owSta?: string;
  toStCh?: Date;
  toSt?: string;
  toSta?: string;
  boStCh?: Date;
  boSt?: string;
  boSta?: string;
  inStCh?: string;
  inSt?: string;
  inSta?: string;
  inCh?: string;
  inGr?: string;
  inFb?: string;
  inFbDa?: string;
  bcSta?: string;
  bcStCh?: Date;
  bcSt?: string;
  isCompliant?: boolean;
};

export type UpsertTaskRequest = {
  status: IdentityTaskStatus;
  ownerId?: string;
  comment?: string | null;
  credentialType: CredentialType;
  appObjectId: string;
};

export type UpsertAppSettingsDto = Omit<AppSettingsDto, 'technicalOwnershipSettings'> & {
  technicalOwnershipSettings: Omit<AppOwnershipSettingsDto, 'technicalOwners'>;
  businessOwnershipSettings: Omit<AppOwnershipSettingsDto, 'businessOwners'>;
};

export type AddPasswordRequest = {
  appType: ResourceType;
  displayName?: string;
  endDateTime: Date;
};

export type AddPasswordResponse = {
  password: PasswordCredentialDto;
};

export type UpdateKeyCredentialsRequest = {
  appType: ResourceType;
  keyCredentials: KeyCredentialDto[];
};

export type UpdateKeyCredentialsResponse = {
  passwordCredentials: PasswordCredentialDto[];
  keyCredentials: KeyCredentialDto[];
};

export type UploadSamlCertificateRequest = {
  certificateBase64: string;
  password: string;
};

export type UploadSamlCertificateResponse = {
  passwordCredentials: PasswordCredentialDto[];
  keyCredentials: KeyCredentialDto[];
};

export type GenerateSamlCertificateRequest = {
  displayName: string;
  endDateTime: Date;
};

export type GenerateSamlCertificateResponse = {
  certificate: KeyCredentialDto;
};

export type AppPolicy = {
  triggerAfterDays: number;
  reminderCount: number;
  reminderTimeIntervalInDays: number;
  escalationActions: string[];
};

export type MinimumOwnersAppPolicy = AppPolicy & {
  minOwners: number;
};

export type MinimumTechnicalOwnersAppPolicy = AppPolicy & {
  minTechnicalOwners: number;
};

export type MinimumBusinessOwnersAppPolicy = AppPolicy & {
  minBusinessOwners: number;
};

export type InactivityAppPolicy = AppPolicy & {
  allowReset: boolean;
};

export type BaselineConfigurationPolicy = AppPolicy & {
  assignmentRequired: boolean | null;
  accountEnabled: boolean | null;
};

export type AppPolicyBundle = {
  id: string;
  minOwnersPolicy: MinimumOwnersAppPolicy | null;
  minTechnicalOwnersPolicy: MinimumTechnicalOwnersAppPolicy | null;
  minBusinessOwnersPolicy: MinimumBusinessOwnersAppPolicy | null;
  inactivityPolicy: InactivityAppPolicy | null;
  baselineConfigurationPolicy: BaselineConfigurationPolicy | null;
};

export type ActivateSamlCertificateRequest = {
  thumbprint: string;
};

export type RemovePasswordRequest = {
  appType: ResourceType;
  keyId: string;
};

export type UpdateSamlCertificateResponse = {
  keyCredentials: KeyCredentialDto[];
  passwordCredentials: PasswordCredentialDto[];
};

export type AddApplicationOwnerRequest = {
  appType: ResourceType;
  userId: string;
};

export type RemoveOwnerRequest = {
  appType: ResourceType;
  userId: string;
};

export type UpdateAppPropertiesRequest = {
  accountEnabled?: boolean;
  appRoleAssignmentRequired?: boolean;
};

export enum ResourceType {
  AppRegistration = 0,
  EnterpriseApplication = 1,
}

export enum IdentityTaskStatus {
  New = 0,
  InProgress = 1,
  Done = 2,
}

/**
 * A credential ID identifies a task only together with its application, as the nightly notification run matches them.
 * Tasks stored before the application ID was recorded have none and match on the credential ID alone.
 */
export function isTaskFor(
  task: IdentityTaskDto,
  keyId: string | null | undefined,
  appObjectId: string | null | undefined
) {
  return (
    sameId(task.id, keyId) &&
    (!task.appObjectId || !appObjectId || task.appObjectId.toLowerCase() === appObjectId.toLowerCase())
  );
}

export enum CredentialType {
  Certificate = 0,
  Secret = 1,
}

/** Mirrors EL.Identity.Business/Settings/ShowAllAppsAccessMode.cs. Admins may always show all apps. */
export enum ShowAllAppsAccessMode {
  Default = 0,
  AllUsers = 1,
  SecurityGroup = 2,
}

export const appPermissions = [
  'editCredentials',
  'editOwners',
  'editTechnicalOwners',
  'editSettings',
  'editBusinessOwners',
  'extendActivity',
  'editProperties',
] as const;

export type AppPermission = (typeof appPermissions)[number];

export type PermissionSet = Record<AppPermission, boolean>;

export const getTechnicalPermissions = (resourceType: ResourceType): AppPermission[] => {
  switch (resourceType) {
    case ResourceType.AppRegistration:
      return ['editCredentials', 'editOwners', 'editTechnicalOwners', 'editSettings', 'editBusinessOwners'];

    case ResourceType.EnterpriseApplication:
      return [
        'editCredentials',
        'editOwners',
        'editTechnicalOwners',
        'editSettings',
        'editBusinessOwners',
        'extendActivity',
        'editProperties',
      ];
  }
};

export const getBusinessPermissions = (resourceType: ResourceType): Extract<AppPermission, 'editBusinessOwners'>[] => {
  switch (resourceType) {
    case ResourceType.AppRegistration:
      return ['editBusinessOwners'];

    case ResourceType.EnterpriseApplication:
      return ['editBusinessOwners'];
  }
};
